/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node, Button, Collapse } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout';
import { getLineItems, ILineItems } from './custom-components/get-line-items';
import { IMandsCheckoutProps } from '../../../modules/mands-checkout/mands-checkout.props.autogenerated';
import { ICheckoutData } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/checkout.data';
import { IMandsCheckoutProps as ICheckoutExtensionProps } from '../definition-extensions/mands-checkout.ext.props.autogenerated';

import get from 'lodash/get';

export interface IState {
    _editLink: React.ReactNode;
    _heading: React.ReactNode;
    isChecked: boolean;
    isOpen: boolean;
}
export interface IRenderLines {
    lineItem: React.ReactNode;
}

interface IOrderSummaryExtendedProps {
    totalItems: number;
    cartUrl: string;
}
/**
 *
 * BuyboxView component
 * @extends {React.PureComponent<ICheckoutViewProps & IMandsCheckoutProps<ICheckoutData>>}
 */

export class CheckoutView extends React.PureComponent<
    ICheckoutViewProps & IMandsCheckoutProps<ICheckoutData> & ICheckoutExtensionProps<{}>,
    IState
> {
    constructor(props: ICheckoutViewProps & IMandsCheckoutProps<ICheckoutData> & ICheckoutExtensionProps<{}>, state: IState) {
        super(props);
        this.state = {
            _editLink: <></>,
            _heading: <></>,
            isChecked: false,
            isOpen: false
        };
    }
    public render(): JSX.Element {
        const {
            canShow,
            checkoutProps,
            bodyProps,
            mainProps,
            mainControlProps,
            sideProps,
            sideControlSecondProps,
            loading,
            alert,
            context: { request },
            guidedForm,
            orderSummary,
            placeOrderButton,
            data: { checkout }
            // keepShoppingButton
        } = this.props;
        let { lineItems } = this.props;
        const lineItemsUpdated = getLineItems(this.props);
        lineItems = lineItemsUpdated;

        const cart = get(checkout, 'result.checkoutCart.cart');
        const totalItems = cart && cart.TotalItems ? cart.TotalItems : 0;
        const totalItemsText = totalItems > 1 ? 'Items' : 'Item';
        const sitePath = request.sitePath ? request.sitePath : '';
        const cartUrl = `${request.url.requestUrl.origin}${sitePath}/cart`;
        const orderTotal = cart && cart.TotalAmount;
        const {
            config: { toolTipText }
        } = this.props;
        const mobileView = request && request.device && request.device.Type === 'Mobile' ? true : false;
        return (
            <Module {...checkoutProps}>
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                <div className='checkout-leftside-wrap'>
                                    {guidedForm}
                                    <div className='terms-btn-wrap term-btn-first'>
                                        <div className='custom-checkbox'>{this.renderCheckBox()}</div>
                                        <Node {...mainControlProps}>
                                            <div className={`placeorder-btn-wrapper ${!this.state.isChecked ? 'disabled' : ''}`}>
                                                {placeOrderButton}
                                                {toolTipText && <span className='placeorder-tooltip'>{toolTipText}</span>}
                                            </div>
                                        </Node>
                                    </div>
                                    {lineItems && <this.LineItemsComponent {...lineItems} />}
                                    <div className='terms-btn-wrap'>
                                        <div className='custom-checkbox'>{this.renderCheckBox()}</div>
                                        <div className={`placeorder-btn-wrapper ${!this.state.isChecked ? 'disabled' : ''}`}>
                                            {placeOrderButton}
                                            {toolTipText && <span className='placeorder-tooltip'>{toolTipText}</span>}
                                        </div>
                                    </div>
                                </div>
                            </Node>
                            <Node {...sideProps}>
                                {mobileView && (
                                    <>
                                        <Button
                                            className={
                                                this.state.isOpen ? 'order-summary-toggle-expanded' : 'order-summary-toggle-collapsed'
                                            }
                                            onClick={this.handleClick}
                                        >
                                            Checkout{' '}
                                            <span className='summary-price-span'>
                                                {totalItems} {totalItemsText}{' '}
                                                <strong>{this.props.context.cultureFormatter.formatCurrency(orderTotal)}</strong>
                                            </span>
                                        </Button>
                                        <Collapse isOpen={this.state.isOpen} timeout={50}>
                                            {orderSummary && (
                                                <this.OrderSummaryComponent cartUrl={cartUrl} totalItems={totalItems} {...orderSummary} />
                                            )}
                                        </Collapse>
                                    </>
                                )}
                                {orderSummary && !mobileView && (
                                    <this.OrderSummaryComponent cartUrl={cartUrl} totalItems={totalItems} {...orderSummary} />
                                )}
                                <Node {...sideControlSecondProps}>
                                    {placeOrderButton}
                                    {/* {keepShoppingButton} */}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            </Module>
        );
    }
    private handleClick = () => {
        this.setState({ isOpen: !this.state.isOpen });
    };
    private toggleChange = () => {
        this.setState({
            isChecked: !this.state.isChecked
        });
    };
    private renderCheckBox = () => {
        const {
            config: { termsConditionsUrl, acceptTermsAndConditionText }
        } = this.props;
        return (
            <label>
                <input
                    type='checkbox'
                    onClick={this.toggleChange}
                    checked={this.state.isChecked}
                    value='terms conditions'
                    aria-checked='true'
                />
                <span className='checkmark' />
                {termsConditionsUrl && termsConditionsUrl.linkText && (
                    <p>
                        {acceptTermsAndConditionText}
                        <a href={termsConditionsUrl.linkUrl.destinationUrl} target='_blank'>
                            <u>{termsConditionsUrl.linkText}</u>
                        </a>
                    </p>
                )}
            </label>
        );
    };
    private PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
        <Node {...PickUpAtStore}>
            {label}
            {location}
        </Node>
    );
    private LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore }) => (
        <Node {...LineItem}>
            {item}
            {pickUpAtStore && <this.PickUpAtStoreComponent {...pickUpAtStore} />}
        </Node>
    );
    private LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = props => {
        const { LineItemDeliveryGroup, LineItemList, lineItems } = props;
        return (
            <Node {...LineItemDeliveryGroup}>
                <Node {...LineItemList}>
                    {lineItems.map(lineItem => (
                        <this.LineItemComponent key={lineItem.LineId} {...lineItem} />
                    ))}
                </Node>
            </Node>
        );
    };
    private LineItemsComponent: React.FC<ILineItems> = props => {
        const { LineItems, Header, heading, itemsForPickup, itemsForShip } = props;
        return (
            <Node {...LineItems}>
                <Node {...Header}>{heading}</Node>
                {itemsForPickup && <this.LineItemGroupComponent {...itemsForPickup} />}
                {itemsForShip && <this.LineItemGroupComponent {...itemsForShip} />}
            </Node>
        );
    };
    private OrderSummaryComponent: React.FC<IOrderSummary & IOrderSummaryExtendedProps> = props => {
        const { lines } = props;
        // const {
        //     config: { helpLink }
        // } = this.props;
        const item = props.totalItems > 1 ? 'Items' : 'Item';
        return (
            <div className='msc-order-summary-wrapper'>
                <div className='msc-order-summary__items'>
                    <div className='item-no-detail'>
                        <h3>
                            {props.totalItems} {item}
                        </h3>
                        <a className='edit-bag' href='cart'>
                            edit bag
                        </a>
                    </div>
                    {lines && (
                        <>
                            {lines.subtotal}
                            {lines.shipping}
                            {lines.totalDiscounts}
                            {lines.loyalty}
                            {lines.giftCard}
                            {lines.orderTotal}
                        </>
                    )}
                </div>
            </div>
        );
    };
}

export default CheckoutView;
